import React, { useEffect, useState } from 'react';
import apiService from '../services/api.service';
import { useTenant } from '../context/TenantContext';
import { useClient } from '../context/ClientContext';
import { useLanguage } from '../context/LanguageContext';
import CookieBanner from './ui/banner-cookie';
import CookiePolicyModal from './ui/modal-cookie';
import { getCookiePreferences, saveCookiePreferences, isConsentExpired } from '../utils/cookieManager';

const CookieConsentManager = () => {
  const { tenant } = useTenant();
  const { client, updateClient, isAuthenticated } = useClient();
  const [show, setShow] = useState(false);

  // Cookie policy config — defaults to enabled banner if absent
  const cookiePolicyEnabled = tenant?.tenantmasterconfig?.cookiepolicy?.enabled !== false;
  const cookiePolicyType = tenant?.tenantmasterconfig?.cookiepolicy?.type || 'banner';

  // Load preferences on mount and when authentication changes
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // Wait for tenant to load
        if (!tenant?.tenantid) {
          return;
        }

        const reconsentDays = tenant?.tenantcontentconfig?.cookies?.reconsentDays || 365;

        // If cookie policy UI is disabled, auto-accept silently and skip all UI
        if (!cookiePolicyEnabled) {
          const existingPrefs = getCookiePreferences(tenant.tenantid, reconsentDays);
          if (!existingPrefs) {
            const policies = { essential: true, analytics: true };
            saveCookiePreferences(tenant.tenantid, policies);
            window.__cookieConsent = policies;
            await sendPreferences(policies);
            window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));
          }
          return;
        }

        if (isConsentExpired(tenant.tenantid, reconsentDays)) {
          setShow(true);
          return;
        }

        // Use cookieManager to get local preferences
        const preferences = getCookiePreferences(tenant.tenantid, reconsentDays);

        // Parse server preferences from client context (if available)
        let serverPrefs = null;
        if (isAuthenticated && client?.cookiePreference) {
          try {
            const parsed = typeof client.cookiePreference === 'string' ? JSON.parse(client.cookiePreference) : client.cookiePreference;
            if (parsed && typeof parsed === 'object') serverPrefs = parsed;
          } catch (e) { /* parse failed */ }
        }

        if (preferences) {
          window.__cookieConsent = preferences;

          // Sync local preferences to server on login, but only if they differ
          if (isAuthenticated && client?.clientId) {
            if (!serverPrefs || serverPrefs.essential !== preferences.essential || serverPrefs.analytics !== preferences.analytics) {
              await sendPreferences(preferences);
            }
          }

          window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: preferences }));
        } else if (serverPrefs) {
          // No local prefs but server has them — use server preferences
          const policies = { essential: serverPrefs.essential, analytics: serverPrefs.analytics };
          saveCookiePreferences(tenant.tenantid, policies);
          window.__cookieConsent = policies;

          window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));
        } else {
          setShow(true);
        }
      } catch (e) {
        console.error('CookieConsentManager - load preferences error', e);
        setShow(true);
      }
    };

    loadPreferences();
  }, [isAuthenticated, client?.clientId, tenant?.tenantid]);

  const sendPreferences = async (policies) => {
    // Only send to API if user is logged in
    if (!isAuthenticated || !client?.clientId) {
      return;
    }

    // Guard: skip API call if tenant context doesn't match authenticated user's tenant
    if (tenant?.tenantid && client?.tenantId && parseInt(tenant.tenantid) !== parseInt(client.tenantId)) {
      console.log('CookieConsentManager - skipping API call: tenant mismatch');
      return;
    }

    const payload = {
      tenantid: tenant?.tenantid || null,
      clientid: client?.clientId || null,
      policies,
    };

    try {
      await apiService.post('/api/clients/cookiepoliciesaction', payload);

      // Update client context with new preference (simple format)
      if (updateClient) {
        const updatedPref = {
          ...policies,
          timestamp: new Date().toISOString()
        };

        updateClient({ cookiePreference: JSON.stringify(updatedPref) });
      }
    } catch (err) {
      console.error('CookieConsentManager - API error', err);

      // If token expired, queue for retry
      // if (err.response?.status === 401) {
      //   setPendingPreference(policies);
      // }
    }
  };

  const handleAccept = async () => {
    const policies = { essential: true, analytics: true };

    // Persist locally using cookieManager
    saveCookiePreferences(tenant?.tenantid, policies);
    window.__cookieConsent = policies;
    setShow(false);

    // Send to API only if logged in
    await sendPreferences(policies);

    // Trigger analytics loading
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));
  };

  const handleDeny = async () => {
    const policies = { essential: true, analytics: false };

    // Persist locally using cookieManager
    saveCookiePreferences(tenant?.tenantid, policies);
    window.__cookieConsent = policies;
    setShow(false);

    // Send to API only if logged in
    await sendPreferences(policies);

    // Trigger update so AnalyticsManager is in sync (e.g., to UNLOAD GTM)
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));
  };

  const handleSaveGranular = async (granularChoices) => {
    const policies = {
      essential: granularChoices.essential !== undefined ? granularChoices.essential : true,
      analytics: granularChoices.analytics !== undefined ? granularChoices.analytics : false,
    };

    // Persist locally using cookieManager
    saveCookiePreferences(tenant?.tenantid, policies);
    window.__cookieConsent = policies;
    setShow(false);

    // Send to API only if logged in
    await sendPreferences(policies);

    // ALWAYS trigger update so AnalyticsManager is in sync
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: policies }));
  };

  if (cookiePolicyType === 'modal') {
    return (
      <CookiePolicyModal
        open={show}
        onOpenChange={setShow}
        onAccept={handleAccept}
        onDeny={handleDeny}
      />
    );
  }

  return (
    <CookieBanner
      show={show}
      onClose={setShow}
      onAccept={handleAccept}
      onDeny={handleDeny}
      onSaveGranular={handleSaveGranular}
    />
  );
};

export default CookieConsentManager;
